// @ts-nocheck
'use client';

import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

type ResolveResponse = {
  ok: boolean;
  run?: {
    id: number;
    join_code: string;
    status?: string | null;
    game_id?: number;
  };
  error?: string;
};

type JoinResponse = {
  ok: boolean;
  run?: {
    id: number;
    join_code: string;
    status?: string | null;
  };
  player?: {
    id: string;
    run_id: number;
    nickname: string | null;
  };
  player_id?: string;
  run_id?: number;
  error?: string;
  message?: string;
};

function StudentJoinInner() {
  const router = useRouter();
  const search = useSearchParams();

  const initialCode = (search.get('code') || '').trim();
  const [code, setCode] = React.useState(initialCode);
  const [nickname, setNickname] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fromQuery = (search.get('code') || '').trim();
    if (fromQuery && !code) setCode(fromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function ensureAuthorId() {
    try {
      let id =
        window.localStorage.getItem(
          'aula:author'
        ) || '';

      if (
        !id ||
        id.length < 6
      ) {
        id =
          typeof crypto !==
            'undefined' &&
          'randomUUID' in crypto
            ? crypto.randomUUID()
            : `anon:${Math.random()
                .toString(36)
                .slice(2, 10)}`;

        window.localStorage.setItem(
          'aula:author',
          id
        );
      }

      return id;
    } catch {
      return `anon:${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    }
  }

  async function tryJoinLiveSession(
    requestedCode: string,
    nickname: string
  ): Promise<boolean> {
    const normalizedCode =
      requestedCode
        .trim()
        .toUpperCase();

    const sessionRes = await fetch(
      '/api/join/' +
        encodeURIComponent(
          normalizedCode
        ),
      {
        credentials: 'include',
        cache: 'no-store',
      }
    );

    if (!sessionRes.ok) {
      return false;
    }

    const session = await sessionRes
      .json()
      .catch(() => null);

    const runId = Number(
      session?.id || 0
    );

    if (
      !Number.isFinite(runId) ||
      runId <= 0
    ) {
      return false;
    }

    const authorId =
      ensureAuthorId();

    try {
      window.localStorage.setItem(
        'aula_nick',
        nickname
      );

      window.localStorage.setItem(
        `aula:run:${runId}:nickname`,
        nickname
      );
    } catch {}

    const nickRes = await fetch(
      '/api/run/' +
        encodeURIComponent(
          String(runId)
        ) +
        '/nickname',
      {
        method: 'POST',
        headers: {
          'content-type':
            'application/json',
        },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({
          authorId,
          nickname,
        }),
      }
    );

    const nickJson = await nickRes
      .json()
      .catch(() => ({}));

    if (!nickRes.ok) {
      throw new Error(
        nickJson?.detail ||
          nickJson?.error ||
          'Failed to save nickname'
      );
    }

    try {
      await fetch(
        `/api/run/${runId}/presence`,
        {
          method: 'POST',
          headers: {
            'content-type':
              'application/json',
          },
          credentials: 'include',
          cache: 'no-store',
          body: JSON.stringify({
            id: authorId,
            nickname,
          }),
        }
      );
    } catch {}

    const key =
      normalizedCode.replace(
        /[\s\-_.]/g,
        ''
      ) ||
      String(
        session?.join_code ||
          session?.joinCode ||
          session?.code ||
          runId
      );

    router.push(
      '/student/present/' +
        encodeURIComponent(key)
    );

    return true;
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedCode = code.trim();
    const normalizedCode = trimmedCode.toUpperCase();
    const trimmedNick = nickname.trim();

    if (!trimmedCode) return setError('Please enter a game code.');
    if (!trimmedNick) return setError('Please enter a nickname.');

    setBusy(true);
    try {
      // 1) Resolve the classroom code -> game_runs row.
      const resolveRes = await fetch(
        '/api/game-runs/resolve/' + encodeURIComponent(normalizedCode),
        { method: 'GET' }
      );

      const resolved: ResolveResponse = (await resolveRes.json().catch(() => ({
        ok: false,
        error: 'resolve_failed',
      }))) as ResolveResponse;

      if (!resolveRes.ok || !resolved.ok || !resolved.run?.id) {
        const joinedSession =
          await tryJoinLiveSession(
            trimmedCode,
            trimmedNick
          );

        if (joinedSession) {
          return;
        }

        const msg =
          resolved.error ||
          'Invalid code. Please check the code with your teacher.';

        throw new Error(msg);
      }

      const resolvedRunId = resolved.run.id;
      const resolvedJoinCode = String(resolved.run.join_code || normalizedCode).trim().toUpperCase();

      // 2) POST join using the classroom code, never the internal numeric run id.
      const joinRes = await fetch(
        '/api/game-runs/' + encodeURIComponent(resolvedJoinCode) + '/join',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nickname: trimmedNick }),
        }
      );

      const joined: JoinResponse = (await joinRes.json().catch(() => ({
        ok: false,
        error: 'join_failed',
      }))) as JoinResponse;

      if (!joinRes.ok || !joined.ok) {
        const msg =
          joined.message ||
          joined.error ||
          'Failed to join this game. Please try again.';
        throw new Error(msg);
      }

      const runId = joined.run?.id ?? joined.run_id ?? resolvedRunId;
      const playerId = joined.player?.id ?? joined.player_id;
      const joinedCode = String(joined.run?.join_code || resolvedJoinCode).trim().toUpperCase();

      // 3) Persist playerId for this run (best-effort)
      try {
        if (typeof window !== 'undefined' && runId && playerId) {
          const legacyKey = `games:${runId}:playerId`;
          const newKey = `aula:game:player:${runId}`;

          window.localStorage.setItem(legacyKey, String(playerId));
          window.localStorage.setItem(newKey, JSON.stringify({ playerId: String(playerId) }));
        }
      } catch {}

      // 4) Go to the student WAIT room using the non-sequential classroom code.
      router.push('/games/student/wait/' + encodeURIComponent(joinedCode));
    } catch (e: any) {
      setError(e?.message || 'Could not join this game.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main
      className="min-h-screen flex items-center justify-center px-4 py-8 bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: "url(/Backgrounds/Waitingroomstudent.png)" }}
    >
      {/* soft overlay so text is readable while keeping the image */}
      <div className="absolute inset-0 bg-sky-50/60 backdrop-blur-sm" aria-hidden="true" />

      <div className="relative w-full flex items-center justify-center">
        <div className="w-full max-w-md rounded-3xl border border-sky-100 bg-white/90 p-6 shadow-2xl text-slate-900">
          <h1 className="text-xl font-semibold mb-1 text-center">Join Game</h1>
          <p className="text-xs text-slate-600 mb-4 text-center">
            Enter the game code from your teacher and choose a nickname.
          </p>

          <form onSubmit={handleJoin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Game code</label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white/90 text-slate-900 px-3 py-2 text-sm font-mono tracking-[0.2em] uppercase placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aula-primary/25 focus-visible:border-aula-primary"
                placeholder="ABC123"
                autoComplete="off"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Nickname</label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white/90 text-slate-900 px-3 py-2 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aula-primary/25 focus-visible:border-aula-primary"
                placeholder="Your name in the game"
                maxLength={40}
              />
            </div>

            {error && (
              <div role="alert" className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-2xl bg-emerald-600 px-3 py-2.5 text-sm font-semibold tracking-wide text-white shadow-md hover:bg-emerald-700 hover:-translate-y-0.5 active:translate-y-0 transition disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aula-primary/35 focus-visible:ring-offset-2"
            >
              {busy ? 'Joining…' : 'Join game'}
            </button>
          </form>

          <p className="mt-4 text-[11px] text-slate-600 text-center">
            Having trouble? Double-check the code or ask your teacher to show the QR again.
          </p>
        </div>
      </div>
    </main>
  );
}

export default function StudentJoinPage() {
  return (
    <React.Suspense
      fallback={
        <main className="min-h-screen bg-slate-950 text-slate-50 flex items-center justify-center px-4 py-8">
          <div className="rounded-3xl border border-slate-800 bg-slate-900/90 px-6 py-4 text-sm text-slate-200 shadow-2xl">
            Loading join screen…
          </div>
        </main>
      }
    >
      <StudentJoinInner />
    </React.Suspense>
  );
}
